import React from "react";
import { func, bool, object, string, exact } from "prop-types";
import { useForm } from "react-hook-form";
import { useTranslation } from "@lib/i18n";

import EditableContainer from "@components/shared/EditableContainer";
import TextField from "@components/shared/TextField";
import Typography from "@components/shared/Typography";
import { Button } from "@components/shared/Button";

import { USER_FIELDS_NAMES } from "@shared/constants";
import { getIsCountry, COUNTRIES } from "../../lib/getIsCountry";

const { COMPANY_INFORMATION } = USER_FIELDS_NAMES;

const CompanyInformation = ({
  onIsEditingChange,
  isEditing,
  onSubmit,
  isTVANumberVisible,
  data: companyDetail,
  regexPatterns,
}) => {
  const { t } = useTranslation();

  const { register, handleSubmit, reset } = useForm({
    defaultValues: companyDetail,
  });

  return (
    <EditableContainer
      title={t("myDetails.companyDetail")}
      isEditing={isEditing}
      onChange={onIsEditingChange}
      summary={
        <div>
          <Typography className="text-sm">
            {companyDetail.companyName}
          </Typography>
          <Typography className="text-sm">
            {companyDetail.siretNumber}
          </Typography>
          {isTVANumberVisible && (
            <Typography className="text-sm">
              {companyDetail.TVANumber}
            </Typography>
          )}
        </div>
      }
      form={
        <form
          className="flex flex-col items-center w-full"
          onSubmit={handleSubmit(data => {
            onSubmit(data);
            reset(data);
          })}
        >
          <TextField
            required
            fullWidth
            ref={register}
            name={COMPANY_INFORMATION.COMPANY_NAME}
            label={t(`myDetails.inputs.${COMPANY_INFORMATION.COMPANY_NAME}`)}
            errorHelperMessage={t(
              `myDetails.helperTexts.${COMPANY_INFORMATION.COMPANY_NAME}`
            )}
            placeholder={t(
              `myDetails.placeholders.${COMPANY_INFORMATION.COMPANY_NAME}`
            )}
          />
          <TextField
            required={!getIsCountry(COUNTRIES.GB)}
            fullWidth
            ref={register}
            name={COMPANY_INFORMATION.SIRET_NUMBER}
            label={t(`myDetails.inputs.${COMPANY_INFORMATION.SIRET_NUMBER}`)}
            title={t(`myDetails.titles.${COMPANY_INFORMATION.SIRET_NUMBER}`)}
            pattern={regexPatterns[COMPANY_INFORMATION.SIRET_NUMBER]}
            errorHelperMessage={t(
              `myDetails.helperTexts.${COMPANY_INFORMATION.SIRET_NUMBER}`
            )}
            placeholder={t(
              `myDetails.placeholders.${COMPANY_INFORMATION.SIRET_NUMBER}`
            )}
          />
          {isTVANumberVisible && (
            <TextField
              fullWidth
              ref={register}
              name={COMPANY_INFORMATION.TVA_NUMBER}
              label={t(`myDetails.inputs.${COMPANY_INFORMATION.TVA_NUMBER}`)}
              title={t(`myDetails.titles.${COMPANY_INFORMATION.TVA_NUMBER}`)}
              pattern={regexPatterns[COMPANY_INFORMATION.TVA_NUMBER]}
              errorHelperMessage={t(
                `myDetails.helperTexts.${COMPANY_INFORMATION.TVA_NUMBER}`
              )}
              placeholder={t(
                `myDetails.placeholders.${COMPANY_INFORMATION.TVA_NUMBER}`
              )}
            />
          )}
          <Button className="text-sm mt-8 mb-2" size="small" type="submit">
            {t("myDetails.inputs.save")}
          </Button>
        </form>
      }
    />
  );
};

CompanyInformation.propTypes = {
  onSubmit: func.isRequired,
  isTVANumberVisible: bool.isRequired,
  regexPatterns: exact({
    [COMPANY_INFORMATION.COMPANY_NAME]: string,
    [COMPANY_INFORMATION.SIRET_NUMBER]: string,
    [COMPANY_INFORMATION.TVA_NUMBER]: string,
  }).isRequired,
  onIsEditingChange: func,
  isEditing: bool,
  data: object,
};

export default CompanyInformation;
